import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const CompanionReady: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useAuth(); // Ensure auth context is available
  const [isCreating, setIsCreating] = useState(false);
  const [companionData, setCompanionData] = useState<any>(null);

  useEffect(() => {
    // Get companion data from session storage
    const data = sessionStorage.getItem('companionData');
    if (data) {
      setCompanionData(JSON.parse(data));
    }
  }, []);

  const handleStartChatting = async () => {
    if (!companionData) {
      console.error('No companion data available');
      return;
    }

    setIsCreating(true);

    try {
      // Create companion via API (axios interceptor adds auth token automatically)
      const skipPreset = sessionStorage.getItem('companionSkipPreset') === 'true';
      const presetId = skipPreset
        ? 'none'
        : companionData.presetId || sessionStorage.getItem('companionPresetId') || undefined;

      const response = await axios.post('/companions', {
        name: companionData.name,
        gender: companionData.gender || 'neutral',
        personality: companionData.personality,
        relationshipType: companionData.relationshipType || 'romantic',
        avatarUrl: companionData.hasCustomAvatar ? null : companionData.avatarUrl,
        presetId,
      });

      const companionId = response.data.id;

      // If there's a custom avatar, upload it
      if (companionData.hasCustomAvatar) {
        const avatarFile = (window as any).companionAvatarFile;
        if (avatarFile) {
          try {
            // Create FormData for upload
            const formData = new FormData();
            formData.append('avatar', avatarFile);

            // Upload avatar
            await axios.post(`/companions/${companionId}/avatar`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });
          } catch (avatarError) {
            console.error('Failed to upload avatar:', avatarError);
            // Continue anyway - companion was created successfully
          }
        }
      }

      // Clear session storage and global references
      sessionStorage.removeItem('companionName');
      sessionStorage.removeItem('companionGender');
      sessionStorage.removeItem('companionRelationshipType');
      sessionStorage.removeItem('companionPersonality');
      sessionStorage.removeItem('companionData');
      sessionStorage.removeItem('avatarFileName');
      sessionStorage.removeItem('companionPresetId');
      sessionStorage.removeItem('avatarPreview');

      // Clear global file reference
      if ((window as any).companionAvatarFile) {
        delete (window as any).companionAvatarFile;
      }

      // Store companion ID for chat
      localStorage.setItem('activeCompanionId', companionId);

      // Navigate to chat with the new companion
      navigate(`/chat/${companionId}`);
    } catch (error: any) {
      console.error('Failed to create companion:', error);

      // Handle specific error cases
      if (error.response?.status === 401) {
        // Token expired or invalid
        alert(
          t('companionCreation.sessionExpired', 'Your session has expired. Please log in again.'),
        );
        navigate('/login');
      } else if (error.response?.data?.code === 'COMPANION_LIMIT_REACHED') {
        // Companion limit reached — redirect to upgrade
        toast.error(
          t(
            'companionCreation.limitReached',
            'You have reached your companion limit. Upgrade your plan for more companions.',
          ),
        );
        navigate('/subscription');
      } else if (error.response?.status === 400) {
        // Duplicate name or other validation error
        toast.error(
          error.response?.data?.message ||
            t('companionCreation.createFailed', 'Failed to create companion. Please try again.'),
        );
        setIsCreating(false);
      } else {
        // Generic error
        alert(t('companionCreation.createFailed', 'Failed to create companion. Please try again.'));
        setIsCreating(false);
      }
    }
  };

  const defaultAvatars: Record<string, { emoji: string; bg: string }> = {
    '1': { emoji: '🌟', bg: 'bg-purple-500' },
    '2': { emoji: '🌙', bg: 'bg-blue-500' },
    '3': { emoji: '🌸', bg: 'bg-pink-500' },
    '4': { emoji: '🍀', bg: 'bg-green-500' },
    '5': { emoji: '🔮', bg: 'bg-indigo-500' },
    '6': { emoji: '🦋', bg: 'bg-teal-500' },
    '7': { emoji: '🌈', bg: 'bg-yellow-500' },
    '8': { emoji: '⚡', bg: 'bg-orange-500' },
  };

  if (!companionData) {
    return (
      <div className="min-h-screen bg-dark-bg dark:bg-dark-bg bg-light-bg flex items-center justify-center">
        <div className="text-dark-text dark:text-dark-text text-light-text">
          {t('common.loading', 'Loading...')}
        </div>
      </div>
    );
  }

  const avatar = defaultAvatars[companionData.avatarUrl] || defaultAvatars['1'];
  const avatarPreview = sessionStorage.getItem('avatarPreview');

  return (
    <div className="min-h-screen bg-dark-bg dark:bg-dark-bg bg-light-bg flex flex-col">
      <div className="mobile-container flex-1 flex flex-col">
        {/* Main content */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 pb-8">
          {/* Success animation placeholder */}
          <div className="mb-8">
            <div className="relative">
              {companionData.hasCustomAvatar && avatarPreview ? (
                <div className="w-24 h-24 rounded-3xl overflow-hidden bg-gray-200">
                  <img
                    src={avatarPreview}
                    alt="Custom avatar"
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div
                  className={`w-24 h-24 rounded-3xl flex items-center justify-center text-5xl ${avatar.bg}`}
                >
                  {avatar.emoji}
                </div>
              )}
              <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold text-dark-text dark:text-dark-text text-light-text mb-2">
            {t('companionCreation.meet', 'Meet {{name}}!', { name: companionData.name })}
          </h1>

          {/* Subtitle */}
          <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary text-center mb-2">
            {t('companionCreation.readyToChat', 'Your companion is ready to chat')}
          </p>

          {/* Personality preview */}
          <div className="bg-dark-surface dark:bg-dark-surface bg-light-surface rounded-2xl p-4 mb-8 max-w-sm">
            <p className="text-sm text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary text-center italic">
              "{companionData.personality}"
            </p>
          </div>

          {/* Features */}
          <div className="space-y-3 mb-12 w-full max-w-sm">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-primary-500 rounded-full" />
              <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary text-sm">
                {t('companionCreation.feature1', '{{name}} will remember your conversations', {
                  name: companionData.name,
                })}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-primary-500 rounded-full" />
              <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary text-sm">
                {t('companionCreation.feature2', 'Adapts to your communication style over time')}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-primary-500 rounded-full" />
              <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary text-sm">
                {t('companionCreation.feature3', 'Always here when you need to talk')}
              </p>
            </div>
          </div>

          {/* Button */}
          <div className="w-full max-w-sm">
            <button
              onClick={handleStartChatting}
              disabled={isCreating}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating
                ? t('companionCreation.creating', 'Creating companion...')
                : t('companionCreation.startChatting', 'Start Chatting')}
            </button>
          </div>

          {/* Tips */}
          <p className="text-xs text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary text-center mt-8 max-w-sm">
            <span className="text-primary-500">{t('companionCreation.tip', 'Tip')}:</span>{' '}
            {t(
              'companionCreation.tipText',
              "You can customize {{name}}'s personality and appearance anytime in Settings",
              { name: companionData.name },
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CompanionReady;
